import classNames from "classnames";
import { EmptyState, ListGroup, ListGroupControl, ListGroupItem, LoadingState, View } from "components/Shared";
import Pagination from "components/Shared/Tcp/Pagination";
import { UserContext } from "context";
import Datasource from "lib/datasource";
import { isFeatureToggled, translate } from "lib";
import _ from "lodash";
import moment from "moment";
import React, { memo, useContext, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Link } from "react-router-dom";

/**
 * The property workspace's "Tenancies" tab — every unit on this property.
 *
 * ---------------------------------------------------------------------------
 * ONE PAGE PER REQUEST, AND IT HAS TO BE (Sim, 1 Sep 2026)
 * ---------------------------------------------------------------------------
 * This list used to be handed `property.tenancies`, an array Properties/Show
 * fetched in one go with `per_page: -1`, and sorted, filtered and searched it in
 * the browser. On a mall that is thousands of units in a single response — and
 * the serializer is not cheap per row: TenancySerializer's occupancy, project
 * and tenant attributes each fold the amendment tree
 * (Tenancy#current_unit_term), and V2::TenanciesController::AMENDMENT_PRELOADS
 * loads every lease agreement, amendment and amendment_tenancy behind them so
 * that fold does not N+1. Multiply that by the whole unit book and the API
 * process runs out of memory. Opening the property page was enough to do it,
 * because the fetch did not wait for the tab to be looked at.
 *
 * So the request is now `page` + `per_page` and this component owns it, the same
 * arrangement as components/Projects/CollectionDynamic and
 * v2/components/LeaseAgreements/Collection.
 *
 * WHAT THAT COSTS, AND WHY IT IS THE RIGHT TRADE:
 *
 *   * SORT_OPTIONS ARE REAL COLUMNS ONLY. SortOptionsGenerator flips to sorting
 *     in Ruby for anything that is not a column, which turns the relation into
 *     an Array — i.e. loads every row, which is the bug. That rules out
 *     `floor`, `building_name` and `occupancy_status`; `name` and `area` stay.
 *   * THE VACANT/OCCUPIED FILTER IS GONE. Occupancy is folded out of the
 *     amendment tree in Ruby, so there is no column to filter on and no honest
 *     way to page it. The column still shows on every row.
 *   * SEARCH IS `?q=`, debounced, and matches unit code, building and floor
 *     server-side. Trading name is folded the same way occupancy is, so it is no
 *     longer searchable here — the Tenants tab beside it answers that question.
 */

// Columns on `tenancies`. See the note above before adding to this list.
//
// "excluded" IS THE ONE THAT IS NOT A COLUMN, and not a sort either (Sim,
// 4 Sep 2026): picking it asks the API for the units Yardi REMOVED with
// "UnitExclude": true, which are soft-deleted and therefore absent from every
// other view of this tab. It rides in the Sort By chips because that is where a
// reader looks for "show me a different set", and reaches the API as its own
// `excluded=true` parameter with the sort left on `name` — see the request below.
const SORT_OPTIONS = ["name", "area", "excluded"];

// The chip value above that means "the excluded units", not an ordering.
const EXCLUDED = "excluded";

const SEARCH_DEBOUNCE_MS = 300;

// `props` is taken whole rather than destructured in the signature: Workspace
// also passes `name` and `icon` to every tab, and a destructured signature in
// a .jsx file makes TypeScript infer those two away as excess props on the
// caller (Properties/Show.tsx would not compile).
const List = (props) => {
	const { property, isAdministrator } = props;
	const { currentUser } = useContext(UserContext);
	const propertyId = _.get(property, "id");
	// null = the first load has not answered yet. An empty array is a legitimate
	// answer, and ListGroup renders the shared empty state for it.
	const [tenancies, setTenancies] = useState(null);
	const [sortBy, setSortBy] = useState("name");
	const [searchTerm, setSearchTerm] = useState("");
	const [query, setQuery] = useState("");
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(
		() =>
			new Datasource({
				mainModelName: "tenancy",
				perPage: 20,
				currentPage: 1,
				sortBy: "name",
				renderTrigger: renderTrigger,
			})
	);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response) => {
			if (response.success) setTenancies(response.normalizedMainModelResponse || []);
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	// DEBOUNCED WITH A setTimeout IN AN EFFECT, not `_.debounce`, and the same
	// shape as components/Projects/useProjectsSearch: this repo's jest runs with
	// legacy fake timers, which mock setTimeout but not Date.now — and lodash's
	// debounce compares the two, so under test it reschedules for ever and never
	// fires. `searchTerm` is what the box shows; `query` is what the API is asked
	// for, one debounce behind.
	useEffect(() => {
		const timer = setTimeout(() => {
			const nextQuery = searchTerm.trim();
			if (nextQuery === query) return;
			unstable_batchedUpdates(() => {
				// Page 4 of everything is rarely page 4 of the matches, and showing an
				// empty page reads as "no results".
				if (datasource.currentPage !== 1) datasource.setCurrentPage(1);
				setQuery(nextQuery);
			});
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchTerm, query, datasource]);

	useEffect(() => {
		// Held back for a non-administrator: the API authorizes this list
		// (PropertyPolicy#administrator?, via `authorize @parent, :tenancies?`) and
		// the 403 that would land in the console is not interesting, the sentence
		// below is.
		if (!propertyId || !isAdministrator) return;
		datasource.get("v2", "tenancies/index_with_joins", {
			// Nils and blanks are OMITTED rather than sent: the query string is built
			// with URLSearchParams, which turns undefined into the literal string
			// "undefined".
			queryStringParameters: _.omitBy(
				{
					property_id: propertyId,
					// "excluded" is a SCOPE, so it travels as its own parameter and the
					// sort falls back to `name` — the API would have nothing to ORDER BY
					// otherwise, and SortOptionsGenerator would load every row trying.
					sort: sortBy === EXCLUDED ? "name" : sortBy,
					excluded: sortBy === EXCLUDED ? true : undefined,
					per_page: datasource.perPage,
					page: datasource.currentPage,
					q: query.trim() || undefined,
				},
				_.isNil
			),
		});
	}, [datasource, datasource.currentPage, datasource.perPage, propertyId, isAdministrator, sortBy, query]);

	// A LIST WITHHELD, NOT AN EMPTY ONE (Sim, 31 Aug 2026). `isAdministrator` is
	// membership of `property.users`, the same set PropertyPolicy#administrator?
	// checks server-side, computed once in Properties/Show. Without this the tab
	// said "No Tenancies found", which reads as a property with no units.
	//
	// Declared AFTER the hooks above, never before: an early return that skips a
	// hook changes the hook order between renders.
	if (!isAdministrator) {
		return (
			<View title="Tenancies">
				<EmptyState>
					<div>{translate("tenancies_restricted_to_property_admin")}</div>
				</EmptyState>
			</View>
		);
	}

	const tenancyToName = (tenancy) => {
		let status = _.get(tenancy, "occupancy_status");
		let tenancyID = _.get(tenancy, "name");
		let currentTradingName = _.get(tenancy, "current_tenant_trading_name");
		let newestTradingName = _.get(tenancy, "newest_unexpired_tenant_trading_name");
		if (status !== "vacant" && (currentTradingName || newestTradingName)) {
			return tenancyID + " — " + (currentTradingName || newestTradingName);
		} else {
			return tenancyID;
		}
	};

	// Project#workflow_status, as a dictionary key. EVERY value it can return is
	// mapped and anything else falls through to "—": the helper used to return
	// undefined for `draft`, `building` and `at_risk`, which rendered an empty
	// cell. Those three were unreachable while only a TCP lease could put a
	// project on a unit; a Yardi lease now can (Tenancy#current_project falls
	// back to the amendment), and a brand-new imported project is `draft`.
	const projectStatusDisplay = (tenancy) => {
		const projectStatus = _.toString(_.get(tenancy, "current_project_status"));
		if (!projectStatus || projectStatus === "vacant") return "—";
		// closed_complete / closed_incomplete — the project is archived either way.
		if (projectStatus.includes("closed")) return "archived";
		return (
			{
				complete: "open_complete",
				behind: "open_behind",
				ahead: "open_ahead",
				at_risk: "at_risk",
				building: "building",
				draft: "draft",
			}[projectStatus] || "—"
		);
	};

	const modelDisplay = (tenancy, model_name) => {
		let model = _.get(tenancy, model_name);
		if (model) {
			return model;
		} else {
			return "—";
		}
	};

	const onSortChange = (value) => {
		datasource.setCurrentPage(1);
		setSortBy(value);
	};

	const onSearchChange = (value) => setSearchTerm(value);

	return (
		<View title="Tenancies">
			<ListGroupControl
				onSortChange={onSortChange}
				selectedSort={sortBy}
				sortOptions={SORT_OPTIONS}
				onSearchChange={onSearchChange}
				searchValue={searchTerm}
				searchLabel="search_tenancies"
			/>
			{tenancies === null ? (
				<LoadingState />
			) : (
				<React.Fragment>
					<ListGroup
						resource="tenancies"
						// ListGroup's own client-side paginator is OFF: it would page within
						// the 20 rows this request returned and present that as the whole
						// set. The one below pages the API.
						pagination={false}
						collection={tenancies}
						renderRow={(tenancy) => (
							<ListGroupItem
								id={tenancy.id}
								key={tenancy.id}
								// THE ROW LINKS ON `current_project_id` ALONE (Sim, 27 Aug 2026).
								// It used to also require the id to be in `projectIds`, the
								// property's project list from `GET /v2/projects?property=` — and
								// that list is `current_user.projects` AND `.finalized`, so it
								// silently withheld the link from exactly the projects this list
								// now surfaces: a Yardi import stays `pending` until someone runs
								// the finalize wizard, and D3 keeps pending projects out of the
								// normal projects index by design. The row was showing the
								// project's status while refusing to open it.
								//
								// One rule across the three screens that offer this jump — the
								// lease's Tenancies tab and the amendment list both link straight
								// to `/projects/:id` with no second list to agree with. The server
								// is the real gate: ProjectPolicy#show? lets a party open a
								// finalized project and a PROPERTY admin open a pending one, which
								// is the wizard's own route in.
								link={
									!!isFeatureToggled("tenancies", currentUser) && !!tenancy.current_project_id
										? `/projects/${tenancy.current_project_id}`
										: null
								}
								heading={tenancyToName(tenancy)}
								datapoints={[
									!!_.get(tenancy, "area") && {
										key: "size",
										value: <React.Fragment>{Math.round(_.get(tenancy, "area")).toLocaleString()}m²</React.Fragment>,
									},
									// PLAIN TEXT, NOT A LINK (Sim, 1 Sep 2026). "Occupied" was a link to
									// the lease agreement occupying the unit for one day; the row itself
									// already navigates — to the project — and a second destination
									// inside it is one too many. `current_lease_agreement_id` is still
									// serialized; nothing on this screen reads it now.
									!!_.get(tenancy, "occupancy_status") && {
										key: "occupancy_status",
										value: <React.Fragment>{_.capitalize(_.get(tenancy, "occupancy_status"))}</React.Fragment>,
									},
									!!_.get(tenancy, "current_project_status") && {
										key: "project_status",
										value: <React.Fragment>{translate(projectStatusDisplay(tenancy))}</React.Fragment>,
									},
									{
										key: "precinct",
										value: <React.Fragment>{modelDisplay(tenancy, "precinct")}</React.Fragment>,
									},
									{
										key: "building_name",
										value: <React.Fragment>{modelDisplay(tenancy, "building_name")}</React.Fragment>,
									},
									{
										key: "floor_name",
										value: <React.Fragment>{modelDisplay(tenancy, "floor_name")}</React.Fragment>,
									},
									{
										key: "",
										value: (
											// stopPropagation, because a row with a project is wrapped in
											// a <Link> by ListGroupItem: without it this Link navigates
											// to the edit form and the click then bubbles to the outer
											// one, which immediately replaces it with the project. Latent
											// until 27 Aug 2026 — the row's `link` was almost always null
											// before the amendment ERD started answering
											// `current_project_id`.
											// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
											<span onClick={(event) => event.stopPropagation()}>
												<Link
													className="btn btn-primary"
													to={{
														pathname: `/tenancies/${tenancy.id}/edit`,
													}}
												>
													<i className="fa fa-pencil" /> Edit
												</Link>
											</span>
										),
									},
								]}
							/>
						)}
					/>
					{!_.isEmpty(tenancies) && (
						<div className={classNames("margin-auto", "flex justify-center")}>
							<Pagination datasource={datasource} />
						</div>
					)}
				</React.Fragment>
			)}
		</View>
	);
};

export default memo(List);
